import { Article } from '@wix/answers-api';
import { GenerationLoader } from '@wix/cc-user-facing-common-components';
import { IconButton, Text, Tooltip } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { Chat as ChatIcon } from '@wix/wix-ui-icons-common';
import classNames from 'classnames';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useRef } from 'react';

import { Resizer } from './Resizer';
import { useResizablePanelWidth } from './Resizer/useResizablePanelWidth';
import { BI, CHATBOT_IFRAME_URL } from '../../../constants';
import { useWindowSize } from '../../../hooks/useWindowSize';
import { useArticleUnifiedBi } from '../../../utils/article-unified-bi';
import { useStore } from '../../../zustand/stores';
import { CopilotDisplayMode } from '../../../zustand/stores/copilot';
import {
  createIframeMessageHandler,
  initChatbotParams,
} from '../../ArticleRightSideBar/iframeMessageHandler';
import { UnhelpfulFeedbackChat } from '../../ArticleRightSideBar/UnhelpfulFeedbackChat';
import { useChatbotMessageSender } from '../../ArticleRightSideBar/useChatbotMessageSender';

import css from './index.module.scss';

const MIN_WIDTH = 400;

type ArticleRightPanelProps = {
  article: Article;
  selfArticleRedirect: (url: string, shallow?: boolean, targetLocale?: string) => void;
  isCollapsed: boolean;
  onCollapse: () => void;
  onExpand: () => void;
  suggestedQuestions?: string[];
};

const noop = () => undefined;

export const ArticleRightPanel = ({
  article,
  selfArticleRedirect,
  isCollapsed,
  onCollapse,
  onExpand,
  suggestedQuestions,
}: ArticleRightPanelProps) => {
  const {
    copilotDisplayMode,
    isChatbotReady,
    hasPendingChatbotMessage,
    setIsChatbotReady,
    setCopilotDisplayMode,
    setChatbotFlow,
    setUnreadMessagesCount,
    chatbotReferral,
    chatbotNodeId,
  } = useStore(state => state.copilotSlice);
  const { selectedSite } = useStore(state => state.siteSelectorSlice);
  const { t } = useTranslation();
  const { locale } = useRouter();
  const { sendMessage } = useChatbotMessageSender({});
  const { helpmatePanelOpenCloseTrack, helpmatePanelMinMaxTrack } =
    useArticleUnifiedBi(article);

  const isLoading = copilotDisplayMode === CopilotDisplayMode.Loading;
  const isUnhelpfulFeedback =
    copilotDisplayMode === CopilotDisplayMode.UnhelpfulFeedback;
  const isActive =
    copilotDisplayMode === CopilotDisplayMode.Chatbot ||
    isLoading ||
    isUnhelpfulFeedback;

  const prevActiveForBiRef = useRef(isActive);
  const windowSize = useWindowSize();
  const maxWidth = useMemo(
    () =>
      windowSize.width
        ? Math.max(MIN_WIDTH, Math.floor(windowSize.width * 0.5))
        : MIN_WIDTH,
    [windowSize.width],
  );
  const { wrapperRef, isResizing, setIsResizing, handleResizeEnd } =
    useResizablePanelWidth({ minWidth: MIN_WIDTH, maxWidth });

  useEffect(() => {
    if (prevActiveForBiRef.current !== isActive) {
      prevActiveForBiRef.current = isActive;
      void helpmatePanelOpenCloseTrack(isActive);
    }
  }, [isActive, helpmatePanelOpenCloseTrack]);

  useEffect(() => {
    if (isLoading && isCollapsed) {
      onExpand();
    }
  }, [isLoading, isCollapsed, onExpand]);

  useEffect(() => {
    if (isChatbotReady && isLoading && !hasPendingChatbotMessage) {
      setCopilotDisplayMode(CopilotDisplayMode.Chatbot);
    }
  }, [
    isChatbotReady,
    isLoading,
    hasPendingChatbotMessage,
    setCopilotDisplayMode,
  ]);

  const handleExpandClick = () => {
    void helpmatePanelMinMaxTrack(false);
    onExpand();
  };

  const handleIframeMessage = useMemo(
    () =>
      createIframeMessageHandler({
        goBack: noop,
        setShowRightSideBar: () => {
          void helpmatePanelMinMaxTrack(true);
          onCollapse();
        },
        setIsChatbotReady,
        selfArticleRedirect,
        setChatbotFlow,
        setUnreadMessagesCount,
        chatbotReferral,
        chatbotNodeId,
        selectedSiteMetaSiteId: selectedSite?.metaSiteId,
        suggestedQuestions,
        chatbotView: BI.CHATBOT_VIEW.HELP_CENTER,
      }),
    [
      onCollapse,
      setIsChatbotReady,
      selfArticleRedirect,
      setChatbotFlow,
      setUnreadMessagesCount,
      chatbotReferral,
      chatbotNodeId,
      selectedSite,
      suggestedQuestions,
      helpmatePanelMinMaxTrack,
    ],
  );

  useEffect(() => {
    window.addEventListener('message', handleIframeMessage);
    return () => window.removeEventListener('message', handleIframeMessage);
  }, [handleIframeMessage]);

  useEffect(() => {
    if (isChatbotReady) {
      initChatbotParams(
        window?.location.href,
        chatbotReferral ?? undefined,
        chatbotNodeId ?? undefined,
        selectedSite?.metaSiteId,
        suggestedQuestions,
        BI.CHATBOT_VIEW.HELP_CENTER,
      );
    }
  }, [suggestedQuestions, isChatbotReady, chatbotReferral, chatbotNodeId, selectedSite]);

  return (
    <div
      ref={wrapperRef}
      className={classNames(css.wrapper, {
        [css.inactive]: !isActive,
        [css.collapsed]: isActive && isCollapsed,
        [css.resizing]: isResizing,
      })}
    >
      {isActive && !isCollapsed && (
        <Resizer
          elemRef={wrapperRef}
          locale={locale}
          minWidth={MIN_WIDTH}
          maxWidth={maxWidth}
          onResizeEnd={handleResizeEnd}
          onResizingChange={setIsResizing}
        />
      )}
      {isActive && (
        <>
          <div
            className={classNames(css.iframeContainer, {
              [css.hidden]: isCollapsed || isLoading || isUnhelpfulFeedback,
            })}
          >
            <iframe
              src={CHATBOT_IFRAME_URL}
              className={css.chatbotIframe}
              id="chatbot-iframe"
            />
          </div>
          {isUnhelpfulFeedback && !isCollapsed && (
            <div className={css.unhelpfulFeedbackPanel}>
              <UnhelpfulFeedbackChat
                article={article}
                sendMessage={sendMessage}
                onCollapse={onCollapse}
              />
            </div>
          )}
          {isLoading && !isCollapsed && (
            <div className={css.loadingPanel}>
              <div className={css.loadingHeader}>
                <IconButton
                  size="small"
                  skin="dark"
                  priority="secondary"
                  disabled
                >
                  <span>
                    <Image
                      src="https://static.parastorage.com/services/kb-client-nextjs/dist/public/images/article-page/box.svg"
                      alt="collapse"
                      width={18}
                      height={18}
                    />
                  </span>
                </IconButton>
              </div>
              <div className={css.loadingContent}>
                <GenerationLoader
                  locale={locale as string}
                  className={css.loadingIcon}
                />
                <Text weight="thin" size="tiny">
                  {t('article-page.copilot.loader.txt')}
                </Text>
              </div>
            </div>
          )}
          {isCollapsed && (
            <div className={css.collapsedPanel}>
              <Tooltip content={t('article-page.right-panel.tooltip')} placement="top">
                <IconButton
                  size="small"
                  skin="dark"
                  priority="secondary"
                  onClick={handleExpandClick}
                >
                  <ChatIcon />
                </IconButton>
              </Tooltip>
            </div>
          )}
        </>
      )}
    </div>
  );
};
